import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, Observable, Subscription, combineLatest, from, of } from 'rxjs';
import { catchError, map, startWith, take } from 'rxjs/operators';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { AuthService } from 'src/app/services/auth.service';
import { User } from 'src/app/auth/user';
import { NotificationService } from 'src/app/shared/notification.service';
import { CommonPopup } from '../common-popup/common-popup';
import { DialogDelete } from 'src/app/dialog-delete/dialog-delete.component';
import { KundeKortService, CUSTOMER_CATEGORIES, CUSTOMER_TYPES, LEAD_SOURCES, leaseStatus } from '../../services/kunde-kort.service';
import { KundekortInfoPopup } from './kunde-kort-popup/kunde-kort-info-popup';
import { BrregService } from '../../services/brreg.service';
import { SystemInfoService } from '../../services/system-info.service';
import { MembershipV2Service } from '../../services/membership-v2.service';
import { AvtalelinjeService } from '../../services/avtalelinje.service';
import { AvstemmingService } from '../../services/avstemming.service';
import { CrmTaskService } from '../../services/crm-task.service';
import { GarantiomsetningService } from '../../services/garantiomsetning.service';
import { ExcelService } from 'src/app/shared/excel';
import { buildKunderExportRows } from './kunder-export';

type QuickFilter = 'all' | 'withSystems' | 'noSystems' | 'missingKundenr' | 'openTasks' | 'lease';

@Component({
  selector: 'app-kundekort',
  templateUrl: './kunde-kort.component.html',
  styleUrls: ['./kunde-kort.component.css']
})
export class KundekortComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  displayedColumns = ['name', 'kundenr', 'category', 'systems', 'groups', 'contract', 'sales', 'lastBilled', 'owner', 'tasks', 'actions'];
  dataSource = new MatTableDataSource<any>([]);

  user: User;
  canEdit = false;
  canDelete = false;
  siteId: string;
  viewMode: 'table' | 'board' = 'table';
  exporting = false;
  loading = true;

  search = '';
  categoryFilter: string | null = null;
  myAccountsOnly = false;
  quickFilter: QuickFilter = 'all';
  // Units (schools, sites) nested under their parent card; off = flat list.
  groupByOrg = true;
  expandedOrgs: { [kundekortId: string]: boolean } = {};
  categoryFilterOptions = CUSTOMER_CATEGORIES;

  // Totals over ALL customers, so the quick-filter chips keep their numbers.
  stats = { all: 0, withSystems: 0, noSystems: 0, missingKundenr: 0, openTasks: 0, lease: 0 };
  filteredCount = 0;

  // Board view and Excel export read the same filtered rows as the table.
  filteredCustomers$ = new BehaviorSubject<any[]>([]);

  // Frigg-app sales (inc. VAT) this month per system_info doc id; filled in
  // the background after the list renders, one aggregate query per system.
  salesMonth = moment().format('YYYY-MM');
  salesLoading = false;
  private salesBySystem = new Map<string, number>();
  private salesRequested = new Set<string>();
  private salesDone: Promise<void> = Promise.resolve();
  private lastSources: any[] = null;

  private allRows: any[] = [];
  private subs: Subscription[] = [];
  dialogSub: Subscription;

  constructor(
    public dialog: MatDialog,
    public auth: AuthService,
    public kundekortService: KundeKortService,
    private notifierService: NotificationService,
    private brregService: BrregService,
    private systemInfoService: SystemInfoService,
    private membershipV2: MembershipV2Service,
    private avtaleSvc: AvtalelinjeService,
    private avstemming: AvstemmingService,
    private taskService: CrmTaskService,
    private garanti: GarantiomsetningService,
    private excel: ExcelService
  ) { }

  ngOnInit() {
    this.subs.push(this.auth.user$.subscribe(user => {
      this.user = user;
      this.canEdit = this.auth.canEdit(user);
      this.canDelete = this.auth.canDelete(user);
      this.applyFilters();
    }));

    // Joined client-side; secondary sources start empty so the list renders
    // as soon as the customers arrive, and fill in as the rest load.
    const soft = <T>(o: Observable<T>, empty: any) => o.pipe(startWith(empty), catchError(() => of(empty)));
    this.subs.push(combineLatest([
      this.kundekortService.getCustomers(),
      soft(this.systemInfoService.systemInfo(), []),
      soft(this.kundekortService.getAllSystemKunder$(), []),
      soft(this.membershipV2.getAllGroupsLive$(), []),
      soft(this.avtaleSvc.list$(), []),
      soft(this.taskService.getOpenTasks$(), []),
      soft(from(this.avstemming.listAllOnce()), []),
    ]).subscribe(sources => {
      this.lastSources = sources;
      this.rebuild();
      this.loadSales(sources[1] as any[], sources[2] as any[]);
    }));

    this.dataSource.sortingDataAccessor = (row, col) => {
      switch (col) {
        case 'name': return String(row.name || '').toLowerCase();
        case 'kundenr': return Number(row.powerOfficeCustomerNo) || 0;
        case 'category': return String(row.category || '');
        case 'systems': return row._systems.length;
        case 'groups': return row._groupCount;
        case 'contract': return row._contractMonthly;
        case 'sales': return row._sales ?? -1;
        case 'lastBilled': return row._lastBilled ? row._lastBilled.total : -1;
        case 'owner': return String(row.ownerName || '').toLowerCase();
        case 'tasks': return row._openTasks;
        default: return row[col];
      }
    };
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.groupByOrg ? null : this.sort;
    this.dataSource.paginator = this.paginator;
  }

  private rebuild() {
    const [customers, systems, links, groups, avtaler, tasks, avstemminger] = this.lastSources as any[][];
    this.allRows = this.buildRows(customers, systems, links, groups, avtaler, tasks, avstemminger);
    this.loading = false;
    this.applyFilters();
  }

  // Sales are per system (transactions.systemId = system_info doc id), so only
  // systems linked to a customer are queried, each once per page visit.
  private loadSales(systems: any[], links: any[]) {
    const docIdByAnyId = new Map<string, string>();
    systems.forEach(s => {
      if (s.systemId) docIdByAnyId.set(String(s.systemId), s.dbid);
      if (s.dbid) docIdByAnyId.set(String(s.dbid), s.dbid);
    });
    const todo = Array.from(new Set(links.map(l => docIdByAnyId.get(String(l.systemId))).filter(Boolean)))
      .filter(id => !this.salesRequested.has(id));
    if (!todo.length) return;
    todo.forEach(id => this.salesRequested.add(id));
    const from = moment(this.salesMonth, 'YYYY-MM').startOf('month').unix();
    const to = moment(this.salesMonth, 'YYYY-MM').endOf('month').unix();
    this.salesLoading = true;
    const queue = [...todo];
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        try { this.salesBySystem.set(id, await this.garanti.computeAppTurnoverIncVat(id, from, to)); }
        catch (e) { console.error('Sales for system failed', id, e); }
      }
    };
    this.salesDone = this.salesDone
      .then(() => Promise.all(Array.from({ length: 8 }, worker)))
      .then(() => {
        this.salesLoading = false;
        if (this.lastSources) this.rebuild();
      });
  }

  private buildRows(customers: any[], systems: any[], links: any[], groups: any[], avtaler: any[], tasks: any[], avstemminger: any[]): any[] {
    const systemById = new Map<string, any>();
    systems.forEach(s => {
      if (s.systemId) systemById.set(String(s.systemId), s);
      if (s.dbid) systemById.set(String(s.dbid), s);
    });

    const linksByCustomer = new Map<string, any[]>();
    links.forEach(l => {
      const list = linksByCustomer.get(l.kundekortId) || [];
      list.push(l);
      linksByCustomer.set(l.kundekortId, list);
    });

    const groupsByCustomer = new Map<string, number>();
    groups.forEach(g => { if (g.customerId) groupsByCustomer.set(g.customerId, (groupsByCustomer.get(g.customerId) || 0) + 1); });

    const thisMonth = moment().format('YYYY-MM');
    const avtaleBySystem = new Map<string, number>();
    avtaler.forEach(l => {
      if (!AvtalelinjeService.dueInMonth(l, thisMonth)) return;
      const key = String(l.systemId);
      avtaleBySystem.set(key, (avtaleBySystem.get(key) || 0) + (Number(l.amountExVat) || 0));
    });

    const now = Date.now();
    const toMs = (v: any) => !v ? 0 : typeof v.toDate === 'function' ? v.toDate().getTime() : new Date(v).getTime();
    const tasksByCustomer = new Map<string, { open: number, overdue: number }>();
    tasks.forEach(t => {
      if (!t.kundekortId) return;
      const e = tasksByCustomer.get(t.kundekortId) || { open: 0, overdue: 0 };
      e.open++;
      const due = toMs(t.dueDate);
      if (due && due < now) e.overdue++;
      tasksByCustomer.set(t.kundekortId, e);
    });

    // Latest saved reconciliation per customer; same month across companies is summed.
    const lastByCustomer = new Map<string, { month: string, total: number }>();
    avstemminger.forEach(d => {
      const month = d.period?.month || '';
      (d.customers || []).forEach((c: any) => {
        if (!c.customerId || c.customerId === 'UNASSIGNED') return;
        const cur = lastByCustomer.get(c.customerId);
        const total = Number(c.billableTotal) || 0;
        if (!cur || month > cur.month) lastByCustomer.set(c.customerId, { month, total });
        else if (month === cur.month) cur.total += total;
      });
    });

    const rows = customers.map(c => {
      const id = c.kundekortId;
      const sysList = (linksByCustomer.get(id) || []).map(l => {
        const s = systemById.get(String(l.systemId));
        return {
          number: s?.number ?? '?',
          name: s?.name || 'System not found',
          active: !!s?.active,
          dbid: s?.dbid || null,
          keys: Array.from(new Set([s?.systemId, s?.dbid, l.systemId].filter(Boolean).map(String))),
          lease: leaseStatus(s?.leaseEndDate),
        };
      }).sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
      // Each system counted once even when lines are keyed by both id spellings.
      const contract = sysList.reduce((sum, s) => sum + s.keys.reduce((m, k) => Math.max(m, avtaleBySystem.get(k) || 0), 0), 0);
      const t = tasksByCustomer.get(id) || { open: 0, overdue: 0 };
      // null until every linked system's sales has loaded; a system linked to
      // several customers counts in full for each of them.
      const sysIds = Array.from(new Set(sysList.map(s => s.dbid).filter(Boolean)));
      const sales = sysIds.every(k => this.salesBySystem.has(k))
        ? +sysIds.reduce((sum, k) => sum + this.salesBySystem.get(k), 0).toFixed(2) : null;
      return {
        ...c,
        _systems: sysList,
        _systemsShown: sysList.slice(0, 3),
        _systemsMore: Math.max(0, sysList.length - 3),
        _inactiveSystems: sysList.filter(s => !s.active).length,
        // Most urgent lease among the customer's systems (ended or ending soon).
        _lease: sysList.map(s => s.lease).filter(l => l && l.state !== 'ok').sort((a, b) => a.days - b.days)[0] || null,
        _groupCount: groupsByCustomer.get(id) || 0,
        _contractMonthly: +contract.toFixed(2),
        _sales: sales,
        _lastBilled: lastByCustomer.get(id) || null,
        _openTasks: t.open,
        _overdueTasks: t.overdue,
        _initials: initials(c.name),
        _search: [c.name, c.legalName, c.orgId, c.powerOfficeCustomerNo, c.email, c.phone, c.city, c.ownerName,
          ...sysList.map(s => `#${s.number} ${s.name}`)].filter(Boolean).join(' ').toLowerCase(),
      };
    });

    // Organisation tree (one level). A unit whose parent card is gone is shown top-level.
    const byId = new Map(rows.map(r => [r.kundekortId, r]));
    rows.forEach(r => {
      const p = r.parentKundekortId ? byId.get(r.parentKundekortId) : null;
      r._parentId = p ? p.kundekortId : null;
      r._parentName = p ? (p.name || '(unnamed)') : '';
      if (p) r._search += ' ' + String(p.name || '').toLowerCase();
    });
    rows.forEach(r => {
      const units = rows.filter(u => u._parentId === r.kundekortId);
      r._unitCount = units.length;
      r._unitSystems = units.reduce((s, u) => s + u._systems.length, 0);
      r._treeGroups = r._groupCount + units.reduce((s, u) => s + u._groupCount, 0);
      r._treeContract = +(r._contractMonthly + units.reduce((s, u) => s + u._contractMonthly, 0)).toFixed(2);
      r._treeSales = [r, ...units].some(x => x._sales == null) ? null : +[r, ...units].reduce((s, x) => s + x._sales, 0).toFixed(2);
      // Latest month among parent + units, summed over everyone billed that month.
      const lasts = [r, ...units].map(x => x._lastBilled).filter(Boolean);
      const month = lasts.reduce((m, l) => l.month > m ? l.month : m, '');
      r._treeLast = month ? { month, total: lasts.filter(l => l.month === month).reduce((s, l) => s + l.total, 0) } : null;
    });
    return rows;
  }

  applyFilters() {
    const q = this.search.trim().toLowerCase();
    const uid = this.user?.uid;
    const base = this.allRows.filter(r =>
      (!this.categoryFilter || r.category === this.categoryFilter) &&
      (!this.myAccountsOnly || (uid && r.ownerId === uid)) &&
      (!q || r._search.includes(q)));

    this.stats = {
      all: base.length,
      withSystems: base.filter(r => r._systems.length).length,
      noSystems: base.filter(r => !r._systems.length).length,
      missingKundenr: base.filter(r => !r.powerOfficeCustomerNo).length,
      openTasks: base.filter(r => r._openTasks).length,
      lease: base.filter(r => r._lease).length,
    };

    const quick = (r: any) => {
      switch (this.quickFilter) {
        case 'withSystems': return r._systems.length > 0;
        case 'noSystems': return r._systems.length === 0;
        case 'missingKundenr': return !r.powerOfficeCustomerNo;
        case 'openTasks': return r._openTasks > 0;
        case 'lease': return !!r._lease;
        default: return true;
      }
    };
    const rows = base.filter(quick);
    this.filteredCount = rows.length;
    this.filteredCustomers$.next(rows);
    this.dataSource.sort = this.groupByOrg ? null : this.sort;
    this.dataSource.data = this.groupByOrg ? this.toTree(rows, !!q || !!this.categoryFilter || this.myAccountsOnly || this.quickFilter !== 'all') : rows.map(r => ({ ...r, _depth: 0 }));
  }

  // Parents (and standalone cards) by name, each followed by its units when
  // expanded. A parent stays visible when only one of its units matches.
  private toTree(matching: any[], filtered: boolean): any[] {
    const match = new Set(matching.map(r => r.kundekortId));
    const name = (r: any) => String(r.name || '').toLowerCase();
    const tops = this.allRows.filter(r => !r._parentId).sort((a, b) => name(a).localeCompare(name(b)));
    const out: any[] = [];
    tops.forEach(p => {
      const units = this.allRows.filter(u => u._parentId === p.kundekortId).sort((a, b) => name(a).localeCompare(name(b)));
      const shownUnits = filtered ? units.filter(u => match.has(u.kundekortId)) : units;
      if (!match.has(p.kundekortId) && !shownUnits.length) return;
      const expanded = this.expandedOrgs[p.kundekortId] ?? (filtered && shownUnits.length > 0);
      out.push({ ...p, _depth: 0, _expanded: expanded, _dimmed: !match.has(p.kundekortId) });
      if (expanded) shownUnits.forEach(u => out.push({ ...u, _depth: 1 }));
    });
    return out;
  }

  toggleOrg(row: any, event: Event) {
    event.stopPropagation();
    this.expandedOrgs[row.kundekortId] = !row._expanded;
    this.applyFilters();
  }

  setGroupByOrg(on: boolean) {
    this.groupByOrg = on;
    this.applyFilters();
  }

  setQuickFilter(f: QuickFilter) {
    this.quickFilter = this.quickFilter === f ? 'all' : f;
    this.applyFilters();
  }

  clearSearch() {
    this.search = '';
    this.applyFilters();
  }

  monthLabel(month: string): string {
    return month ? moment(month, 'YYYY-MM').format('MMM YY') : '';
  }

  trackRow(_: number, row: any) { return row.kundekortId + ':' + row._depth; }

  addKunde() {

    const dialogRef = this.dialog.open(CommonPopup, {
      disableClose: true,
      width: '500px',
      data: {
        siteId: this.siteId,
        formData: {
          title: "New customer",
          inputFeilds: [
            {
              labelName: "Customer type",
              name: "customerType",
              required: true,
              type: null,
              value: CUSTOMER_TYPES[1],
              selectOptions: CUSTOMER_TYPES.map(c => ({ value: c, label: c })),
              specialText: ""
            },
            {
              labelName: "Name",
              name: "name",
              required: true,
              type: "text",
              value: null,
              specialText: ""
            },
            {
              labelName: "Email",
              name: "email",
              required: true,
              type: "text",
              value: null,
              specialText: ""
            },
            {
              labelName: "Phone",
              name: "phone",
              required: true,
              type: "number",
              value: null,
              specialText: ""
            },
            {
              labelName: "Website",
              name: "web",
              required: false,
              type: "text",
              value: null,
              specialText: ""
            },
            {
              labelName: "Org. number",
              name: "orgId",
              required: true,
              type: "text",
              value: null,
              specialText: "",
              lookup: {
                buttonLabel: "Look up",
                handler: (orgNumber: string) => this.brregService.lookup(orgNumber)
                  .then(result => ({
                    name: result.name,
                    address: result.address,
                    address2: result.address2,
                    zipCode: result.zipCode,
                    city: result.city,
                    country: result.country,
                  }))
              }
            },
            {
              labelName: "Address",
              name: "address",
              required: true,
              type: "text",
              value: null,
              specialText: ""
            },
            {
              labelName: "Address 2",
              name: "address2",
              required: false,
              type: "text",
              value: null,
              specialText: ""
            },
            {
              labelName: "Zip code",
              name: "zipCode",
              required: false,
              type: "text",
              value: null,
              specialText: ""
            },
            {
              labelName: "City",
              name: "city",
              required: false,
              type: "text",
              value: null,
              specialText: ""
            },
            {
              labelName: "Country",
              name: "country",
              required: false,
              type: "text",
              value: null,
              specialText: ""
            },
            {
              labelName: "Category",
              name: "category",
              required: true,
              type: null,
              value: CUSTOMER_CATEGORIES[0],
              selectOptions: CUSTOMER_CATEGORIES.map(c => ({ value: c, label: c })),
              specialText: ""
            },
            {
              labelName: "Lead source",
              name: "leadSource",
              required: false,
              type: null,
              value: null,
              selectOptions: [{ value: null, label: '— None —' }, ...LEAD_SOURCES.map(c => ({ value: c, label: c }))],
              specialText: ""
            }

          ],
          buttons: [
            {
              name: "Add",
              type: "submit",
              color: "primary",
              validate: true
            }
          ]
        }
      }
    });

    this.dialogSub = dialogRef.afterClosed().subscribe(async result => {

      

      if (result) {
        const newUserData = result.value;
        // Check if any data was entered
        if((!newUserData.name)){
          this.notifierService.showNotification("error", "Employee id or name needs to be entered!")
          return;
        }else{
          // A duplicate org number is a question, not an error: one organisation can
          // have several invoice recipients (see KundeKortService.addCustomer).
          this.kundekortService.addCustomer(newUserData)
            .then(ref => this.customerAdded(ref, newUserData))
            .catch(err => {
              if (err?.code === 'DUPLICATE_ORG' && confirm(`${err.message}\n\nCreate this customer anyway?`)) {
                this.kundekortService.addCustomer(newUserData, true)
                  .then(ref => this.customerAdded(ref, newUserData))
                  .catch(e => this.notifierService.showNotification('error', e?.message || 'Could not add customer'));
                return;
              }
              this.notifierService.showNotification('error', err?.message || 'Could not add customer');
            });
        }
      }
    });

    // this.router.navigate(['system/new'],{relativeTo:this.route})

  }



  // An Active customer gets its PowerOffice customer + Kundenr straight away.
  private customerAdded(ref: any, data: any) {
    this.notifierService.showNotification('success', 'Customer added');
    if (data.category !== 'Active' || !ref?.id) return;
    this.kundekortService.autoPowerOffice(ref.id)
      .then(r => r && this.notifierService.showNotification(r.type, r.message));
  }

  // Excel of the customers currently listed (honours every filter): every
  // kundekort field incl. the PowerOffice customer no, plus joined contacts,
  // linked systems and billed groups.
  async exportExcel() {
    if (this.exporting) return;
    this.exporting = true;
    try {
      await this.salesDone;
      const once = <T>(o: Observable<T>) => o.pipe(take(1)).toPromise();
      const [contacts, systems, links, groups] = await Promise.all([
        this.kundekortService.getAllContactsOnce().catch(() => []),
        once(this.systemInfoService.systemInfo()).catch(() => []),
        once(this.kundekortService.getAllSystemKunder$()).catch(() => []),
        once(this.membershipV2.getAllGroupsLive$()).catch(() => [])
      ]);
      const rows = buildKunderExportRows(this.filteredCustomers$.value, contacts, systems as any[], links as any[], groups as any[], this.salesMonth);
      if (!rows.length) {
        this.notifierService.showNotification('error', 'No customers to export');
        return;
      }
      await this.excel.exportAsExcelFile(rows, 'kunder');
    } catch (e) {
      console.error('Kunder export failed', e);
      this.notifierService.showNotification('error', 'Could not export customers');
    } finally {
      this.exporting = false;
    }
  }

  deleteKunde(row: any, event?: Event) {
    event?.stopPropagation();
    const dialogRef = this.dialog.open(DialogDelete, {
      disableClose: true,
      width: '350px',
      data: {
        formData: {
          title: 'DELETE',
          message: `Delete customer "${row.name || 'without name'}"? Are you sure?`,
          buttons: ['Cancel', 'Yes']
        }
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.kundekortService.deleteCustomer(row.kundekortId);
    });
  }

  showDetail(row: any) {
    const data = { ...row };
    Object.keys(data).filter(k => k.startsWith('_')).forEach(k => delete data[k]);
    this.dialog.open(KundekortInfoPopup, {
      width: '95vw',
      maxWidth: '1440px',
      height: '92vh',
      data,
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    if (this.dialogSub) this.dialogSub.unsubscribe();
  }
}

function initials(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}
